import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { mandantenAPI, reportsAPI } from '../lib/api';

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (n === 0) return '';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(n);
}

function currentMonthRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(lastDay) };
}

export default function Ustva() {
  const { slug } = useParams();
  const init = currentMonthRange();
  const [mandant, setMandant] = useState(null);
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [includeVorschlag, setIncludeVorschlag] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reload = useCallback(() => {
    setLoading(true);
    return reportsAPI
      .ustva(slug, { from, to, include_vorgeschlagen: includeVorschlag })
      .then((r) => setResult(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'UStVA konnte nicht berechnet werden')
      )
      .finally(() => setLoading(false));
  }, [slug, from, to, includeVorschlag]);

  useEffect(() => { reload(); }, [reload]);

  const steuerschuld = result ? Number(result.steuerschuld) : 0;
  const steuerschuldLabel = steuerschuld >= 0 ? 'Zahllast (ans Finanzamt)' : 'Erstattung (vom Finanzamt)';
  const steuerschuldClass = steuerschuld >= 0 ? 'badge badge-review' : 'badge badge-gebucht';

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Umsatzsteuer-Voranmeldung</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <Link to={`/mandanten/${slug}/reports`} className="btn-text">
            ← SuSa
          </Link>
          <Link to={`/mandanten/${slug}/reports/opl`} className="btn-text">
            Offene Posten →
          </Link>
        </div>
      </header>

      <div className="filters" style={{ flexWrap: 'wrap' }}>
        <label>
          Von: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Bis: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={includeVorschlag}
            onChange={(e) => setIncludeVorschlag(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Vorschläge einbeziehen
        </label>
        <button className="btn-text" onClick={reload} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : !result ? (
        <div className="card empty">Keine Daten.</div>
      ) : (
        <>
          {/* Hauptkennzahlen — ELSTER-Positionen */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginTop: 0 }}>ELSTER-Kennzahlen</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '8ch' }}>Pos</th>
                  <th>Bezeichnung</th>
                  <th className="num">Bemessungsgrundlage</th>
                  <th className="num">Steuer</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>81</b></td>
                  <td>Steuerpflichtige Umsätze zu 19%</td>
                  <td className="num">{fmtMoney(result.umsaetze_19_netto)}</td>
                  <td className="num">{fmtMoney(result.umsaetze_19_ust)}</td>
                </tr>
                <tr>
                  <td><b>86</b></td>
                  <td>Steuerpflichtige Umsätze zu 7%</td>
                  <td className="num">{fmtMoney(result.umsaetze_7_netto)}</td>
                  <td className="num">{fmtMoney(result.umsaetze_7_ust)}</td>
                </tr>
                <tr>
                  <td>—</td>
                  <td className="muted">Steuerfreie Umsätze (Sammelposition)</td>
                  <td className="num">{fmtMoney(result.umsaetze_steuerfrei)}</td>
                  <td className="num muted">—</td>
                </tr>
                <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 600 }}>
                  <td>—</td>
                  <td>Umsatzsteuer (gesamt)</td>
                  <td className="num muted">—</td>
                  <td className="num">{fmtMoney(result.umsatzsteuer_total)}</td>
                </tr>
                <tr>
                  <td><b>66</b></td>
                  <td>Vorsteuerbeträge (gesamt)</td>
                  <td className="num muted">—</td>
                  <td className="num">{fmtMoney(result.vorsteuer_total)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700, background: '#f8fafc' }}>
                  <td>—</td>
                  <td>
                    {steuerschuldLabel}{' '}
                    <span className={steuerschuldClass} style={{ marginLeft: 8 }}>
                      {steuerschuld >= 0 ? 'zahlen' : 'erstatten'}
                    </span>
                  </td>
                  <td className="num muted">—</td>
                  <td className="num">{fmtMoney(Math.abs(steuerschuld))}</td>
                </tr>
              </tbody>
            </table>
            <p className="muted small" style={{ marginTop: 12, marginBottom: 0 }}>
              Hinweis: Vorabberechnung auf Basis der Buchungen im Zeitraum.
              Keine ELSTER-Übertragung — Werte zur Kontrolle vor offizieller Abgabe.
              Pos 35/41 (steuerfreie ig. Lieferungen / sonstige) sind in v1 nicht getrennt.
            </p>
          </div>

          {/* Detailaufschlüsselung pro USt-Kennzeichen */}
          <h3>Detail pro USt-Kennzeichen</h3>
          {result.details.length === 0 ? (
            <div className="card empty">Keine Buchungen mit USt-Kennzeichen im Zeitraum.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kennz.</th>
                  <th>Bezeichnung</th>
                  <th>Seite</th>
                  <th className="num">Satz</th>
                  <th className="num">Brutto</th>
                  <th className="num">Netto</th>
                  <th className="num">USt</th>
                  <th className="num">Anz.</th>
                </tr>
              </thead>
              <tbody>
                {result.details.map((d) => (
                  <tr key={d.ust_kennz}>
                    <td><b>{d.ust_kennz}</b></td>
                    <td>{d.label}</td>
                    <td className="muted">{d.seite}</td>
                    <td className="num">{d.satz ? `${Number(d.satz)}%` : '—'}</td>
                    <td className="num">{fmtMoney(d.brutto_summe)}</td>
                    <td className="num">{fmtMoney(d.netto_summe)}</td>
                    <td className="num">{fmtMoney(d.ust_summe)}</td>
                    <td className="num muted">{d.anzahl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
